import { Button, GoalStepper } from '@components'
import { useAIHelpContext, useBusinessContext } from '@context'
import { MarkdownText } from '@root/containers/MarkdownText'
import { BusinessOnboardingStepKey, IBusinessOnboardStep } from '@query'
import { completeOnboardStep, dismissOnboardStep, undismissOnboardStep } from '@query/onboard'
import { isFeaturebaseEnabled, showFeaturebaseNewMessage } from '@utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import styled from 'styled-components'
import { HearAboutUsModal } from './HearAboutUsModal'
import { AccrualAccountingModal } from './AccrualAccountingModal'
import { getStepCredits } from './companionCredits'
import { getGroupStatus, isGroupHandled } from './companionStepState'
import type { GetStartedBankReturnKind } from './useBankHandover'

const SKIPPABLE_STEPS = new Set([
  BusinessOnboardingStepKey.BANK_INTEGRATION,
  BusinessOnboardingStepKey.HEAR_ABOUT_US,
  BusinessOnboardingStepKey.FILES_AND_INVOICES,
  BusinessOnboardingStepKey.LUCA_MESSAGE
])

interface Props {
  step: IBusinessOnboardStep
  onBankCta: () => void
  onGoToTransactions: () => void
  showBankSuccess: boolean
  bankReturnKind: GetStartedBankReturnKind | null
  onResolved: (groupKey: BusinessOnboardingStepKey, kind: 'done' | 'skipped') => void
  onCollapse: () => void
}

export const ActiveStepCard: React.FC<Props> = ({
  step,
  onBankCta,
  onGoToTransactions,
  showBankSuccess,
  bankReturnKind,
  onResolved,
  onCollapse
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const stepId = step.child_steps[0]?.id
  const status = getGroupStatus(step)
  const isSkipped = status === 'skipped'
  const handled = isGroupHandled(step)
  const canSkip = !handled && SKIPPABLE_STEPS.has(step.group_key)
  const canUnskip = isSkipped && SKIPPABLE_STEPS.has(step.group_key)
  const showSelfReportActions =
    !handled &&
    (step.group_key === BusinessOnboardingStepKey.MOBILE_APP_DOWNLOAD ||
      step.group_key === BusinessOnboardingStepKey.HAS_PREVIOUS_ACCOUNTING ||
      step.group_key === BusinessOnboardingStepKey.QUESTIONS)
  const showBankActions = !handled && step.group_key === BusinessOnboardingStepKey.BANK_INTEGRATION
  const showHearAboutUsActions =
    !handled && step.group_key === BusinessOnboardingStepKey.HEAR_ABOUT_US
  const showAccrualActions =
    !handled && step.group_key === BusinessOnboardingStepKey.ACCRUAL_ACCOUNTING
  const showLucaActions = !handled && step.group_key === BusinessOnboardingStepKey.LUCA_MESSAGE
  const showApproveTransactionsActions =
    !handled && step.group_key === BusinessOnboardingStepKey.APPROVE_TRANSACTIONS
  const showActions =
    showBankActions ||
    showHearAboutUsActions ||
    showAccrualActions ||
    showLucaActions ||
    showApproveTransactionsActions ||
    showSelfReportActions ||
    canSkip
  const [hearAboutUsOpen, setHearAboutUsOpen] = React.useState(false)
  const [accrualOpen, setAccrualOpen] = React.useState(false)
  const { setAIHelpOpen } = useAIHelpContext()

  const refreshBusiness = () => queryClient.invalidateQueries({ queryKey: [slug], exact: true })

  const { mutateAsync: completeStep, isPending: isCompleting } = useMutation({
    mutationFn: () => completeOnboardStep({ slug, stepId: stepId as number }),
    onError: () => toast.error(t('general.error')),
    onSuccess: async () => {
      onResolved(step.group_key, 'done')
      await refreshBusiness()
    }
  })

  const { mutateAsync: dismissStep, isPending: isDismissing } = useMutation({
    mutationFn: () => dismissOnboardStep({ slug, stepId: stepId as number }),
    onError: () => toast.error(t('general.error')),
    onSuccess: async () => {
      onResolved(step.group_key, 'skipped')
      await refreshBusiness()
    }
  })

  const { mutateAsync: undismissStep, isPending: isUndismissing } = useMutation({
    mutationFn: () => undismissOnboardStep({ slug, stepId: stepId as number }),
    onError: () => toast.error(t('general.error')),
    onSuccess: async () => {
      await refreshBusiness()
    }
  })

  const onLucaCta = () => {
    setAIHelpOpen(true)
    completeStep()
  }

  if (showBankSuccess) {
    const isHolviReturn = bankReturnKind === 'holvi'
    return (
      <Details>
        <SuccessTitle>
          {t(
            isHolviReturn ? 'bankOnboarding.complete.holviTitle' : 'bankOnboarding.complete.title'
          )}
        </SuccessTitle>
        <SuccessText>
          {t(
            isHolviReturn
              ? 'bankOnboarding.complete.holviDescription'
              : 'bankOnboarding.complete.description1'
          )}
        </SuccessText>
        <SuccessText>
          {t(
            isHolviReturn
              ? 'bankOnboarding.complete.holviDescription2'
              : 'bankOnboarding.complete.description2'
          )}
        </SuccessText>
        <Actions>
          <Button type="button" intent="primary" size="sm" onClick={onGoToTransactions}>
            {t('bankOnboarding.complete.goToTransactions')}
          </Button>
        </Actions>
      </Details>
    )
  }

  if (!stepId) return null

  return (
    <Details>
      <Description>
        <MarkdownText text={step.description} />
      </Description>
      {step.group_key === BusinessOnboardingStepKey.FILES_AND_INVOICES && (
        <NestedGoalStepper>
          <GoalStepper
            goals={step.child_steps.map(child => ({
              content: <MarkdownText text={child.label} />,
              isCompleted: child.completed_at !== null
            }))}
          />
        </NestedGoalStepper>
      )}
      {canUnskip && (
        <Actions>
          <Button
            type="button"
            isSecondary
            size="sm"
            showSpinner={isUndismissing}
            disabled={isUndismissing}
            onClick={() => undismissStep()}
          >
            {t('dashboard.getStartedWidget.unskip')}
          </Button>
        </Actions>
      )}
      {showActions && (
        <Actions>
          {showBankActions && (
            <Button type="button" intent="primary" size="sm" onClick={onBankCta}>
              {t('dashboard.onboard.addBankIntegration')}
            </Button>
          )}
          {showApproveTransactionsActions && (
            <Button
              type="button"
              intent="primary"
              size="sm"
              onClick={step.has_bank_connection ? onGoToTransactions : onBankCta}
            >
              {t(
                step.has_bank_connection
                  ? 'bankOnboarding.complete.goToTransactions'
                  : 'dashboard.onboard.addBankIntegration'
              )}
            </Button>
          )}
          {showAccrualActions && (
            <Button type="button" intent="primary" size="sm" onClick={() => setAccrualOpen(true)}>
              {t('dashboard.getStartedWidget.accrualAccounting.cta')}
            </Button>
          )}
          {showHearAboutUsActions && (
            <Button
              type="button"
              intent="primary"
              size="sm"
              onClick={() => setHearAboutUsOpen(true)}
            >
              {t('dashboard.getStartedWidget.hearAboutUs.cta')}
            </Button>
          )}
          {showLucaActions && (
            <Button
              type="button"
              intent="primary"
              size="sm"
              showSpinner={isCompleting}
              disabled={isCompleting}
              onClick={onLucaCta}
            >
              {t('dashboard.getStartedWidget.lucaMessage.cta')}
            </Button>
          )}
          {showSelfReportActions && (
            <>
              <Button
                type="button"
                intent="primary"
                size="sm"
                showSpinner={isCompleting}
                disabled={isCompleting}
                onClick={() => completeStep()}
              >
                {t('general.done')}
              </Button>
              {step.group_key === BusinessOnboardingStepKey.HAS_PREVIOUS_ACCOUNTING && (
                <Button
                  type="button"
                  isSecondary
                  size="sm"
                  onClick={() => {
                    showFeaturebaseNewMessage(t('dashboard.onboard.supportMessage'))
                    if (isFeaturebaseEnabled()) {
                      onCollapse()
                    }
                  }}
                >
                  {t('dashboard.onboard.iNeedHelp')}
                </Button>
              )}
            </>
          )}
          {canSkip && (
            <Button
              type="button"
              isSecondary
              size="sm"
              showSpinner={isDismissing}
              disabled={isDismissing}
              onClick={() => dismissStep()}
            >
              {t('general.skip')}
            </Button>
          )}
        </Actions>
      )}
      {showHearAboutUsActions && (
        <HearAboutUsModal
          isVisible={hearAboutUsOpen}
          stepId={stepId}
          creditReward={getStepCredits(step)}
          onClose={() => setHearAboutUsOpen(false)}
          onResolved={onResolved}
        />
      )}
      {showAccrualActions && (
        <AccrualAccountingModal
          isVisible={accrualOpen}
          stepId={stepId}
          onClose={() => setAccrualOpen(false)}
          onResolved={onResolved}
        />
      )}
    </Details>
  )
}

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) =>
    `0 ${theme.spacing.sm}rem ${theme.spacing.sm}rem ${theme.spacing.sm}rem`};
`

const Description = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};

  & * {
    margin-bottom: 0;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const SuccessTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.dashboard.positive};
`

const SuccessText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.metalGray};
`

const NestedGoalStepper = styled.div`
  svg {
    width: ${({ theme }) => theme.iconSize.xs}rem;
    height: ${({ theme }) => theme.iconSize.xs}rem;
  }
`
